import * as React from "react";
import set from "lodash/set";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import Portal from "components/common/Portal";
import { FormControl, FormGroup, Input, BootstrapModalWrapper, Modal } from "components/bootstrap";
import ModalSubmit from "components/common/ModalSubmit";
import FormikInput from "components/common/FormikInput";
import { Icon, Select, HoverForHelp } from "components/common";
import FrequencySummary from "report/report-configuration-page/FrequencySummary";
import { isWeeklyFrequency, isMonthlyFrequency } from "report/typeGuards/reportingWidget";
import useUserDateTime from "hooks/useUserDateTime";
import FrequencyControls from "./FrequencyControls";
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
export const FREQUENCY_OPTIONS = {
  HOURLY: {
    value: "HOURLY",
    label: "frequency_options.hourly"
  },
  DAILY: {
    value: "DAILY",
    label: "frequency_options.daily"
  },
  WEEKLY: {
    value: "WEEKLY",
    label: "frequency_options.weekly"
  },
  MONTHLY: {
    value: "MONTHLY",
    label: "frequency_options.monthly"
  }
};
const frequencyOptions = Object.values(FREQUENCY_OPTIONS);
export const frequencies = Object.keys(FREQUENCY_OPTIONS);
const frequencyHelpText = (frequency) => {
  switch (frequency) {
    case FREQUENCY_OPTIONS.HOURLY.value:
      return i18n.t("frequency_form.help.hourly", { ns: "enterprise_report" });
    case FREQUENCY_OPTIONS.DAILY.value:
      return i18n.t("frequency_form.help.daily", { ns: "enterprise_report" });
    case FREQUENCY_OPTIONS.WEEKLY.value:
      return i18n.t("frequency_form.help.weekly", { ns: "enterprise_report" });
    case FREQUENCY_OPTIONS.MONTHLY.value:
      return i18n.t("frequency_form.help.monthly", { ns: "enterprise_report" });
    default:
      return /* @__PURE__ */ React.createElement("div", null);
  }
};
const validateForm = (values) => {
  var _a, _b, _c;
  let errors = {};
  if (!values.frequency) {
    errors = { frequency: i18n.t("frequency_form.frequency_required", { ns: "enterprise_report" }) };
  }
  if (values.frequency) {
    if (!((_a = values.frequency_configuration) == null ? void 0 : _a.time)) {
      errors = set(
        errors,
        "frequency_configuration.time",
        i18n.t("frequency_form.time_required", { ns: "enterprise_report" })
      );
    }
    if (isWeeklyFrequency(values) && !((_b = values.frequency_configuration) == null ? void 0 : _b.day_of_week)) {
      errors = set(
        errors,
        "frequency_configuration.day_of_week",
        i18n.t("frequency_form.day_required", { ns: "enterprise_report" })
      );
    }
    if (isMonthlyFrequency(values) && !((_c = values.frequency_configuration) == null ? void 0 : _c.day_of_month)) {
      errors = set(
        errors,
        "frequency_configuration.day_of_month",
        i18n.t("frequency_form.day_required", { ns: "enterprise_report" })
      );
    }
  }
  return errors;
};
const resetConfigOnFrequencySelect = (newFrequency, setFieldValue, userTimezone) => {
  const updateFrequencyConfig = (newFrequencyConfig) => setFieldValue("frequency_configuration", newFrequencyConfig);
  switch (newFrequency) {
    case "DAILY":
    case "WEEKLY":
    case "MONTHLY":
      updateFrequencyConfig({ timezone: userTimezone });
      return;
    default:
      updateFrequencyConfig(void 0);
  }
};
const SchedulingFrequencyForm = ({
  schedulingFrequency = void 0,
  onSubmit,
  onCancel,
  title,
  submitButtonText,
  submitLoadingText
}) => {
  const { t } = useTranslation("enterprise_report");
  const { userTimezone } = useUserDateTime();
  return /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(BootstrapModalWrapper, { showModal: true, onHide: onCancel }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: schedulingFrequency != null ? schedulingFrequency : {},
      onSubmit,
      validate: validateForm,
      validateOnBlur: false,
      validateOnMount: true
    },
    ({ isSubmitting, isValidating, isValid, values, setFieldValue }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, title != null ? title : t("frequency_form.title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Field, { name: "frequency" }, ({ field: { name, value, onChange, onBlur }, meta: { error, touched } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error && touched ? "error" : void 0,
        help: t("frequency_form.frequency_help"),
        error: error && touched ? error : void 0,
        id: name,
        label: t("frequency_form.frequency_label")
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          onBlur,
          onChange: (newValue) => {
            if (newValue !== value) {
              resetConfigOnFrequencySelect(newValue, setFieldValue, userTimezone);
            }
            onChange({
              target: { value: newValue, name }
            });
          },
          options: frequencyOptions.map((option) => ({ ...option, label: t(option.label) })),
          inputId: name,
          placeholder: t("frequency_form.placeholder"),
          required: true,
          value
        }
      )
    )), values.frequency && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "frequency-options-preview",
        label: t("frequency_form.frequency_options_label", {
          frequency: t(FREQUENCY_OPTIONS[values.frequency].label)
        }),
        help: frequencyHelpText(values.frequency)
      },
      /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FrequencyControls, null))
    ), /* @__PURE__ */ React.createElement(FormControl.Static, { className: "text-info" }, /* @__PURE__ */ React.createElement(Icon, { name: "info" }), " ", /* @__PURE__ */ React.createElement(
      FrequencySummary,
      {
        frequencyConfiguration: values.frequency_configuration,
        frequency: values.frequency
      }
    )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        id: "frequency_configuration.override_widgets_timerange",
        name: "frequency_configuration.override_widgets_timerange",
        type: "checkbox",
        label: t("frequency_form.override_timerange_label")
      }
    ), /* @__PURE__ */ React.createElement(HoverForHelp, { title: t("frequency_form.override_timerange_title") }, t("frequency_form.override_timerange_help"))))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        onCancel,
        disabledSubmit: isValidating || !isValid,
        isSubmitting,
        isAsyncSubmit: true,
        submitLoadingText,
        submitButtonText
      }
    )))
  )));
};
export default SchedulingFrequencyForm;
